/**
 * Who is signed in, as a tiny external store (useSyncExternalStore), so the
 * data source can read the actor synchronously outside React.
 *
 * Two modes:
 *  - 'api':  a real session token from /auth/verify;
 *            `me` comes from GET /me and is re-read on every app start.
 *  - 'demo': no server at all. A demo person of the chosen persona; it can
 *            be switched from the profile screen. Stored under its own key so
 *            it never mixes with a real token. The personas (shared with
 *            the native app in @fsv/data) match the seed; the data provider re-reads them from the
 *            demo store after start and after platform writes.
 *
 * `me` is cross-tenant (ADR 0004): roles live in `me.memberships`, one per
 * tenant; the active tenant comes from the URL, not from the session.
 */
import { api } from '../api';
import { DEMO_ROLES, demoPerson, isDemoRole, isMe, type DemoRole, type Me } from '@fsv/data';
import { demoEntryFromUrl } from '../lib/demoEntry';

// The personas are shared with the native app (@fsv/data, demoPersonas.ts); re-exported for the screens.
export { DEMO_ROLES, demoPerson, isDemoRole, isMe, type DemoRole };

export interface Session {
  mode: 'api' | 'demo';
  me: Me;
  /** Demo only: which persona is active, for the switcher. */
  demoRole?: DemoRole | undefined;
}

export type SessionState = { status: 'loading' } | { status: 'signed-out' } | { status: 'signed-in'; session: Session };

const DEMO_KEY = 'fsv.demo.role';

function readDemoRole(): DemoRole | null {
  try {
    const v = window.localStorage.getItem(DEMO_KEY);
    return isDemoRole(v) ? v : null;
  } catch {
    return null;
  }
}

function writeDemoRole(role: DemoRole | null): void {
  try {
    if (role) window.localStorage.setItem(DEMO_KEY, role);
    else window.localStorage.removeItem(DEMO_KEY);
  } catch {
    /* private mode: demo lasts for this tab only */
  }
}

let state: SessionState = { status: 'loading' };
const listeners = new Set<() => void>();

function set(next: SessionState): void {
  state = next;
  for (const l of listeners) l();
}

export const sessionStore = {
  get: (): SessionState => state,
  subscribe(listener: () => void): () => void {
    listeners.add(listener);
    return () => listeners.delete(listener);
  },
  /** The current actor for the data source; null when signed out. */
  me(): Me | null {
    return state.status === 'signed-in' ? state.session.me : null;
  },
  /** Replaces `me` after memberships changed (GET /api/me again, or the demo store). Ignored for another person. */
  setMe(me: Me): void {
    if (state.status !== 'signed-in' || state.session.me.memberId !== me.memberId) return;
    if (JSON.stringify(state.session.me) === JSON.stringify(me)) return;
    set({ status: 'signed-in', session: { ...state.session, me } });
  },
};

export function startDemo(role: DemoRole): void {
  writeDemoRole(role);
  set({ status: 'signed-in', session: { mode: 'demo', me: demoPerson(role), demoRole: role } });
}

export function signedInWithApi(token: string, me: Me): void {
  writeDemoRole(null);
  api.setToken(token);
  set({ status: 'signed-in', session: { mode: 'api', me } });
}

/**
 * Runs once at start, before the router reads the URL: a demo link
 * (`/demo`, `/demo/mitglied|technik|admin`, or the older alias
 * `?demo=member|editor|admin` on any route) wins, then a stored
 * demo role, then a stored API token (validated with GET /me). A demo start
 * never touches the network.
 */
export async function bootstrapSession(): Promise<void> {
  const entry = demoEntryFromUrl(window.location.pathname, window.location.search);
  if (entry) {
    window.history.replaceState(null, '', `${entry.path}${window.location.hash}`);
    startDemo(entry.role);
    return;
  }
  const demo = readDemoRole();
  if (demo) {
    startDemo(demo);
    return;
  }
  await restoreApiSession();
}

/** The stored real session, validated with GET /me; signed out without a token or when it is no longer valid. */
async function restoreApiSession(): Promise<void> {
  if (!api.getToken()) {
    set({ status: 'signed-out' });
    return;
  }
  try {
    const me = await api<unknown>('/me');
    if (isMe(me)) set({ status: 'signed-in', session: { mode: 'api', me } });
    else set({ status: 'signed-out' });
  } catch {
    // 401 clears the token inside the client; offline keeps it for the next start.
    set({ status: 'signed-out' });
  }
}

/** A real session waits behind the demo (opened from the welcome, the profile or a demo link while signed in). */
export function hasRealSessionBehindDemo(): boolean {
  return state.status === 'signed-in' && state.session.mode === 'demo' && api.getToken() !== null;
}

/**
 * "Demo beenden". Someone who opened a demo link while signed in for real
 * goes back to that session (its token was never touched by the demo);
 * anyone else is signed out. Returns where the person now is.
 */
export async function endDemo(): Promise<'api' | 'signed-out'> {
  writeDemoRole(null);
  if (!api.getToken()) {
    set({ status: 'signed-out' });
    return 'signed-out';
  }
  set({ status: 'loading' });
  await restoreApiSession();
  return state.status === 'signed-in' ? 'api' : 'signed-out';
}

api.onUnauthorized(() => {
  if (state.status === 'signed-in' && state.session.mode === 'api') set({ status: 'signed-out' });
});

export async function signOut(scope: 'this' | 'all'): Promise<void> {
  // By the token, not by the mode: a person who opened the tour from a real
  // session is in demo mode with the token still stored („Zurück zu meinem
  // Verein“). Signing out there used to keep it, and the next person at that
  // browser was signed in as them (security review 15.10.2026).
  if (api.getToken()) {
    try {
      await api(scope === 'all' ? '/auth/signout/all' : '/auth/signout', { method: 'POST' });
    } catch {
      /* signing out locally must work offline too */
    }
    api.clearToken();
  }
  writeDemoRole(null);
  set({ status: 'signed-out' });
}
